import React, { useState, useEffect } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Settings,
  Globe,
  Zap,
  ExternalLink,
  Shield,
  Users,
  FolderPlus
} from 'lucide-react';
import api from '../utils/api';


const IntegrationSetupWizard = ({ isOpen, onClose, user, selectedIntegration }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setConnectionStatus('idle');
      setErrorMessage('');
    }
  }, [isOpen]);

  const getIntegrationSteps = () => {
    if (!selectedIntegration) {
      return [
        { id: 1, title: 'Select Integration', description: 'Choose an integration to setup' }
      ];
    }

    switch (selectedIntegration.id) {
      case 'highlevel':
        return [
          { id: 1, title: 'Welcome', description: 'Setup GoHighLevel integration', icon: <Settings className="w-5 h-5" /> },
          { id: 2, title: 'Connect', description: 'Authorize GoHighLevel access', icon: <Globe className="w-5 h-5" /> },
          { id: 3, title: 'Configure', description: 'Configure data sync settings', icon: <Zap className="w-5 h-5" /> },
          { id: 4, title: 'Complete', description: 'Integration setup complete', icon: <CheckCircle className="w-5 h-5" /> }
        ];
      case 'google-workspace':
        return [
          { id: 1, title: 'Welcome', description: 'Setup Google Workspace integration', icon: <Settings className="w-5 h-5" /> },
          { id: 2, title: 'Connect', description: 'Authorize Google access', icon: <Globe className="w-5 h-5" /> },
          { id: 3, title: 'Configure Drives', description: 'Setup organization drives', icon: <FolderPlus className="w-5 h-5" /> },
          { id: 4, title: 'Complete', description: 'Integration setup complete', icon: <CheckCircle className="w-5 h-5" /> }
        ];
      case 'microsoft-365':
        return [
          { id: 1, title: 'Welcome', description: 'Setup Microsoft 365 integration', icon: <Settings className="w-5 h-5" /> },
          { id: 2, title: 'Connect', description: 'Authorize Microsoft access', icon: <Globe className="w-5 h-5" /> },
          { id: 3, title: 'Configure OneDrive', description: 'Setup organization OneDrive', icon: <FolderPlus className="w-5 h-5" /> },
          { id: 4, title: 'Complete', description: 'Integration setup complete', icon: <CheckCircle className="w-5 h-5" /> }
        ];
      case 'zoom':
        return [
          { id: 1, title: 'Welcome', description: 'Setup Zoom integration', icon: <Settings className="w-5 h-5" /> },
          { id: 2, title: 'Connect', description: 'Authorize Zoom access', icon: <Globe className="w-5 h-5" /> },
          { id: 3, title: 'Complete', description: 'Integration setup complete', icon: <CheckCircle className="w-5 h-5" /> }
        ];
      default:
        return [
          { id: 1, title: 'Welcome', description: `Setup ${selectedIntegration.name} integration`, icon: <Settings className="w-5 h-5" /> },
          { id: 2, title: 'Connect', description: `Authorize ${selectedIntegration.name} access`, icon: <Globe className="w-5 h-5" /> },
          { id: 3, title: 'Complete', description: 'Integration setup complete', icon: <CheckCircle className="w-5 h-5" /> }
        ];
    }
  };

  const steps = getIntegrationSteps();

  const handleConnect = async () => {
    if (!selectedIntegration) return;

    setLoading(true);
    setConnectionStatus('connecting');
    
    try {
      let endpoint = '';
      
      switch (selectedIntegration.id) {
        case 'highlevel':
          endpoint = '/integrations/gohighlevel/authorize';
          break;
        case 'google-workspace':
          endpoint = '/integrations/google/authorize';
          break;
        case 'microsoft-365':
          endpoint = '/microsoft/oauth/authorize';
          break;
        case 'zoom':
          endpoint = '/integrations/zoom/authorize';
          break;
        default:
          throw new Error(`Unknown integration: ${selectedIntegration.id}`);
      }

      const response = await api.get(`${endpoint}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });

      if (response.data.authorization_url) {
        // Store current step for when user returns
        sessionStorage.setItem('integration_setup_step', currentStep.toString());
        sessionStorage.setItem('integration_setup_type', selectedIntegration.id);
        
        // Redirect to OAuth
        window.location.href = response.data.authorization_url;
      } else {
        throw new Error('No authorization URL received');
      }

    } catch (error) {
      console.error('Connection error:', error);
      setConnectionStatus('error');
      setErrorMessage(error.message || 'Failed to start connection process');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    if (!selectedIntegration) {
      return (
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No integration selected</p>
        </div>
      );
    }

    switch (currentStep) {
      case 1:
        return (
          <div className="text-center py-8">
            <div className="text-6xl mb-6">{selectedIntegration.icon}</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Welcome to {selectedIntegration.name}
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {selectedIntegration.description}
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-lg mx-auto">
              <div className="flex items-start space-x-3">
                <Shield className="w-6 h-6 text-blue-600 mt-1" />
                <div className="text-left">
                  <h4 className="font-semibold text-blue-900 mb-2">Organization-Level Integration</h4>
                  <p className="text-sm text-blue-800">
                    This integration will be set up for your entire organization. All team members will have access to shared data, folders, and features.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="text-center py-8">
            <Globe className="w-16 h-16 text-blue-600 mx-auto mb-6" />
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Connect Your {selectedIntegration.name} Account
            </h3>
            <p className="text-gray-600 mb-6">
              You'll be redirected to {selectedIntegration.name} to authorize access for your organization.
            </p>
            
            {connectionStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div className="text-left">
                    <h5 className="font-medium text-red-900">Connection Error</h5>
                    <p className="text-sm text-red-800 mt-1">{errorMessage}</p>
                  </div>
                </div>
              </div>
            )}

            <button 
              onClick={handleConnect}
              disabled={loading}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Connecting...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Connect to {selectedIntegration.name}
                </>
              )}
            </button>
          </div>
        );

      case 3:
        return (
          <div className="text-center py-8">
            <FolderPlus className="w-16 h-16 text-green-600 mx-auto mb-6" />
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Organization Setup Complete
            </h3>
            <p className="text-gray-600 mb-6">
              Your {selectedIntegration.name} integration has been configured for organization-level access.
            </p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 max-w-lg mx-auto">
              <div className="text-left">
                <h4 className="font-semibold text-green-900 mb-3">What's Next:</h4>
                <ul className="text-sm text-green-800 space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                    Shared folders will be created automatically
                  </li>
                  <li className="flex items-center">
                    <Users className="w-4 h-4 text-green-600 mr-2" />
                    All team members can access shared data
                  </li>
                  <li className="flex items-center">
                    <Zap className="w-4 h-4 text-green-600 mr-2" />
                    Data sync is now active
                  </li>
                </ul>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Setup Complete!
            </h3>
            <p className="text-gray-600 mb-6">
              Your {selectedIntegration.name} integration is now active and ready to use.
            </p>
            
            <button 
              onClick={onClose}
              className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Done
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {selectedIntegration && (
              <>
                <div className="text-2xl">{selectedIntegration.icon}</div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Setup {selectedIntegration.name}
                  </h2>
                  <p className="text-sm text-gray-600">Organization Integration</p>
                </div>
              </>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div 
                key={step.id}
                className={`flex items-center ${index < steps.length - 1 ? 'flex-1' : ''}`}
              >
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  currentStep >= step.id 
                    ? 'bg-blue-600 border-blue-600 text-white' 
                    : 'border-gray-300 text-gray-400'
                }`}>
                  {step.icon || <span className="text-xs font-semibold">{step.id}</span>}
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    currentStep > step.id ? 'bg-blue-600' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="mt-2">
            <p className="text-sm font-medium text-gray-900">
              {steps[currentStep - 1]?.title}
            </p>
            <p className="text-xs text-gray-500">
              {steps[currentStep - 1]?.description}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <button
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </button>
          
          <span className="text-sm text-gray-500">
            Step {currentStep} of {steps.length}
          </span>
          
          <button
            onClick={() => setCurrentStep(Math.min(steps.length, currentStep + 1))}
            disabled={currentStep === steps.length}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default IntegrationSetupWizard;